import React, { useMemo } from 'react';
import useAppStore from '../store/appStore';
import { Page, Section, PageTitle } from '../components/UI';

// ============================================================================
// STYLES: EXECUTIVE CARBON EDITION (NOBLE SLATE v11.0)
// ============================================================================
const DashboardStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        
        .bg-deep-space { background-color: #030303 !important; }
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary) !important; }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); }
        html:not(.dark) .carbon-panel::after { background: none; }
        .text-phantom-gold { color: #E6C299; }
        
        .carbon-weave-bg {
            background-color: #111;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-console, .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }

        .carbon-console::after, .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6));
            border-radius: inherit;
            pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        .metal-badge {
            background: linear-gradient(to bottom, #222, #0a0a0a);
            border: 1px solid #333;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 10px rgba(0,0,0,0.8);
            border-radius: 6px;
        }

        /* ========================================================================
           FINALER FIX: BLAUER AUSSEN-WRAPPER WIRD SCHWARZ
           ======================================================================== */

        html,
        body,
        #root,
        #__next,
        main {
            background-color: #030303 !important;
            background: #030303 !important;
        }

        body:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) #root,
        body:has(.contact-carbon-shell) #__next,
        body:has(.contact-carbon-shell) main,
        body:has(.contact-carbon-shell) div:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) section:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="page"]:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="Page"]:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="layout"]:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="Layout"]:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="wrapper"]:has(.contact-carbon-shell),
        body:has(.contact-carbon-shell) [class*="Wrapper"]:has(.contact-carbon-shell) {
            background-color: #030303 !important;
            background: #030303 !important;
            background-image: none !important;
        }

        .contact-carbon-shell {
            background-color: #030303 !important;
            background: #030303 !important;
            min-height: 100vh;
        }

        .contact-carbon-shell > *,
        .contact-carbon-shell section,
        .contact-carbon-shell section > div,
        .contact-carbon-shell [class*="bg-slate-900"],
        .contact-carbon-shell [class*="bg-slate-950"],
        .contact-carbon-shell [class*="bg-blue-"],
        .contact-carbon-shell [class*="bg-indigo-"],
        .contact-carbon-shell [class*="bg-gray-900"],
        .contact-carbon-shell [class*="bg-gray-950"] {
            background-color: #030303 !important;
        }

        .contact-carbon-shell .carbon-panel {
            background-color: initial;
        }

        .contact-carbon-shell .carbon-weave-bg {
            background-color: #111 !important;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent) !important;
            background-size: 20px 20px !important;
        }

        .contact-carbon-shell .bg-black {
            background-color: #000000 !important;
        }

        .contact-carbon-shell .bg-slate-800,
        .contact-carbon-shell div.bg-slate-800,
        .contact-carbon-shell [class*="bg-slate-800"] {
            background-color: #1e293b !important;
            background-image: none !important;
        }

        /* ============================================================
           LIGHT-MODE OVERRIDES (Ring 4b) — Dark Mode bleibt pixelidentisch,
           da alle Regeln nur unter html:not(.dark) greifen.
           ============================================================ */
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary) !important; }
        html:not(.dark) .text-phantom-gold { color: #8D6435; }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-console, html:not(.dark) .carbon-panel {
            background-color: var(--pe-bg-card);
            border-color: var(--pe-border-subtle);
            box-shadow: 0 10px 30px -12px rgba(28,25,23,0.15);
        }
        html:not(.dark) .carbon-panel::after, html:not(.dark) .carbon-console::after { background: none; }
        html:not(.dark) .carbon-console::after, html:not(.dark) .carbon-panel::after { background: none; }
        html:not(.dark) .metal-badge {
            background: linear-gradient(to bottom, #FFFFFF, #F1EFEA);
            border: 1px solid #D6D3D1;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 4px 10px rgba(28,25,23,0.08);
        }
        html:not(.dark),
        html:not(.dark) body,
        html:not(.dark) #root,
        html:not(.dark) #__next,
        html:not(.dark) main {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
        }
        html:not(.dark) body:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) #root,
        html:not(.dark) body:has(.contact-carbon-shell) #__next,
        html:not(.dark) body:has(.contact-carbon-shell) main,
        html:not(.dark) body:has(.contact-carbon-shell) div:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) section:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="page"]:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="Page"]:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="layout"]:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="Layout"]:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="wrapper"]:has(.contact-carbon-shell),
        html:not(.dark) body:has(.contact-carbon-shell) [class*="Wrapper"]:has(.contact-carbon-shell) {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
            background-image: none !important;
        }
        html:not(.dark) .contact-carbon-shell {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
        }
        html:not(.dark) .contact-carbon-shell > *,
        html:not(.dark) .contact-carbon-shell section,
        html:not(.dark) .contact-carbon-shell section > div,
        html:not(.dark) .contact-carbon-shell [class*="bg-slate-900"],
        html:not(.dark) .contact-carbon-shell [class*="bg-slate-950"],
        html:not(.dark) .contact-carbon-shell [class*="bg-blue-"],
        html:not(.dark) .contact-carbon-shell [class*="bg-indigo-"],
        html:not(.dark) .contact-carbon-shell [class*="bg-gray-900"],
        html:not(.dark) .contact-carbon-shell [class*="bg-gray-950"] {
            background-color: transparent !important;
        }
        html:not(.dark) .contact-carbon-shell .carbon-weave-bg {
            background-color: #FFFFFF !important;
            background-image: none !important;
        }
        html:not(.dark) .contact-carbon-shell .bg-slate-800,
        html:not(.dark) .contact-carbon-shell div.bg-slate-800,
        html:not(.dark) .contact-carbon-shell [class*="bg-slate-800"] {
            background-color: #F1EFEA !important;
            background-image: none !important;
        }
    `}</style>
);

// Icons
const MailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
);

const WhatsAppIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.570-.010c-.198 0-.520.074-.792.372-.272.297-1.040 1.017-1.040 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.200 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.360.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.290.173-1.414z"/></svg>
);

const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);

const PEBASE_URL = 'https://pebase.friseur-coaching.de/';
const PEBASE_VIDEO = '/assets/pebase/pebase-anleitung.mp4';

const WhatsAppEmergencyNote = () => (
    <p className="text-[10px] text-stone-500 dark:text-slate-400 leading-relaxed font-bold text-center">
        WhatsApp ist nicht mehr unsere Kommunikationsebene — nur noch für den Notfall.
        Die Kommunikation läuft ausschließlich über{' '}
        <a href={PEBASE_URL} target="_blank" rel="noopener noreferrer" className="text-pe-accent-text dark:text-[#E6C299] underline underline-offset-2">PEBase</a>.
    </p>
);

const ContactPage: React.FC = () => {
    const { clientData, team, teamRegistry, appSettings } = useAppStore(state => ({
        clientData: state.clientData,
        team: state.team,
        teamRegistry: state.teamRegistry,
        appSettings: state.appSettings
    }));
    
    const cleanWhatsAppNumber = (number: string | undefined): string => {
        if (!number) return '';
        return number.replace(/[^0-9+]/g, '');
    };

    const clientContactInfo = [
        { type: `Mail`, label: clientData?.mailadresse ?? '', link: `mailto:${clientData?.mailadresse}`, icon: MailIcon },
        { type: `WhatsApp`, label: clientData?.whatsappNr ?? '', link: `https://wa.me/${cleanWhatsAppNumber(clientData?.whatsappNr)}`, icon: WhatsAppIcon },
    ];
    
    const getCategoryLabel = (category: string | undefined) => {
        switch(category) {
            case 'webdesign': return 'Webdesign & Kommunikation';
            case 'support': return 'Technischer Support & IT';
            case 'coach': return 'Business Coach & Experte';
            default: return 'Coach & Experte';
        }
    };

    const teamContactInfo = useMemo(() => {
        const activeTeam = Array.isArray(team) ? team : [];
        const registry = Array.isArray(teamRegistry) ? teamRegistry : [];
        const activeKeys = new Set(
            activeTeam.flatMap((m: any) => {
                const keys: string[] = [];
                if (m?.id) keys.push(String(m.id));
                if (m?.email) keys.push(String(m.email).trim().toLowerCase());
                return keys;
            })
        );

        const fromTeam = activeTeam
            .filter((member: any) => {
                const email = String(member?.email || '').trim().toLowerCase();
                const name = String(member?.name || '').trim().toLowerCase();
                if (email === 'test@preisexpert.de') return false;
                if (name.includes('testo')) return false;
                if (member?.isTest === true || member?.excludeFromBilling === true) return false;
                return true;
            })
            .map((member: any) => {
                return {
                    id: member.id || member.email || member.name,
                    name: member.name,
                    firstName: String(member.name || '').split(' ')[0],
                    phone: member.phone,
                    link: `https://wa.me/${cleanWhatsAppNumber(member.phone)}`,
                    image: member.imageUrl,
                    role: getCategoryLabel(member.category),
                    customRole: member.role,
                    calendarLinkBasic: member.calendarLinkBasic || '',
                    calendarLinkInsider: member.calendarLinkInsider || '',
                    calendarLinkTeam: member.calendarLinkTeam || '',
                };
            });

        const fromRegistry = registry
            .filter((member: any) => {
                const id = member?.id ? String(member.id) : '';
                const email = String(member?.email || '').trim().toLowerCase();
                if (id && activeKeys.has(id)) return false;
                if (email && activeKeys.has(email)) return false;
                const name = String(member?.name || '').trim().toLowerCase();
                if (!name && !email) return false;
                if (email === 'test@preisexpert.de') return false;
                if (name.includes('testo')) return false;
                return true;
            })
            .map((member: any) => {
                return {
                    id: `ext-${member.id || member.email || member.name}`,
                    name: member.name,
                    firstName: String(member.name || '').split(' ')[0],
                    phone: member.phone,
                    link: member.phone ? `https://wa.me/${cleanWhatsAppNumber(member.phone)}` : '',
                    image: member.imageUrl,
                    role: 'Externer Kontakt',
                    customRole: member.role,
                    calendarLinkBasic: member.calendarLinkBasic || '',
                    calendarLinkInsider: member.calendarLinkInsider || '',
                    calendarLinkTeam: member.calendarLinkTeam || '',
                };
            });

        return [...fromTeam, ...fromRegistry];
    }, [team, teamRegistry]);

    const generalContactInfo = {
        name: "Zentrale / Support",
        value: "erfolg@friseur-coaching.de",
        link: "mailto:erfolg@friseur-coaching.de"
    };
    
    const isBasicMode = clientData?.lizenzDetails?.typ === 'basic';
    const systemLogoUrl = isBasicMode ? appSettings.basicLogoUrl : appSettings.logoUrl;
    const logoToDisplay = systemLogoUrl || clientData?.logo;

    return (
        <Page>
            <DashboardStyles />
            <div className="contact-carbon-shell bg-deep-space min-h-screen font-sans text-pe-text dark:text-slate-200 selection:bg-[#E6C299] selection:text-black pb-12 p-4 sm:p-8">
                <Section> 
                    <PageTitle><span className="tracking-widest uppercase text-pe-text dark:text-white">Kontakt & Meetings</span></PageTitle>

                    {/* HEADER BEREICH */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                        
                        {/* DEINE KONTAKTDATEN */}
                        <div className="lg:col-span-1 carbon-panel carbon-weave-bg p-6 shadow-2xl relative overflow-hidden group border-pe-border dark:border-[#2a2a2a]">
                            <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299] shadow-[0_0_15px_rgba(230,194,153,0.6)]"></div>
                            <div className="carbon-content">
                                <h4 className="text-lg font-black text-pe-text dark:text-white tracking-widest uppercase mb-6 pl-2">Deine Daten</h4>
                                <div className="space-y-4">
                                    {clientContactInfo.map((contact, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-stone-100 dark:bg-black border border-pe-border dark:border-[#222] hover:border-pe-border-strong dark:hover:border-[#333] transition-colors shadow-inner">
                                            <div className="flex items-center gap-3 overflow-hidden w-full">
                                                <div className="text-stone-500 dark:text-slate-500 flex-shrink-0 drop-shadow-[0_0_5px_rgba(255,255,255,0.1)]"><contact.icon /></div>
                                                <div className="flex flex-col overflow-hidden w-full">
                                                    <span className="text-[10px] uppercase text-stone-500 dark:text-slate-500 font-black tracking-widest">{contact.type}</span>
                                                    <span className="text-sm text-pe-accent-text dark:text-[#E6C299] font-mono-numbers truncate block" title={contact.label}>{contact.label || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* HINWEIS */}
                        <div className="lg:col-span-2 carbon-panel bg-gradient-to-br from-emerald-100/70 to-stone-100/40 dark:from-emerald-950/20 dark:to-black/40 border border-emerald-300 dark:border-emerald-500/20 p-6 shadow-2xl flex flex-col justify-center">
                            <div className="carbon-content flex items-start gap-4">
                                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 drop-shadow-[0_0_10px_rgba(16,185,129,0.4)]">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <div>
                                    <h4 className="text-xl font-black text-emerald-700 dark:text-emerald-400 tracking-wider uppercase mb-2">24/7 Beratungsflatrate</h4>
                                    <div className="text-pe-muted dark:text-slate-300 text-sm space-y-2 leading-relaxed">
                                        <p>Du darfst Dich jederzeit bei uns melden – wir antworten dir so schnell wie möglich!</p>
                                        <p>Auch wenn du einen festen Betreuer hast, steht dir das <strong className="text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-wider">gesamte Team</strong> zur Verfügung.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h4 className="text-xl lg:text-2xl font-black text-pe-text dark:text-white mb-6 flex items-center gap-3 border-t border-pe-border dark:border-[#222] pt-8 tracking-widest uppercase">
                        <span className="bg-[#E6C299] w-2 h-8 rounded-full inline-block shadow-[0_0_10px_rgba(230,194,153,0.5)]"></span>
                        Unser Team
                    </h4>
                    
                    {/* ZENTRALE + PEBASE — ein gemeinsamer Block */}
                    <div className="carbon-panel carbon-weave-bg border border-pe-border dark:border-[#2a2a2a] hover:border-[#E6C299]/30 rounded-2xl p-6 sm:p-8 shadow-2xl mb-12">
                        <div className="carbon-content grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-10 items-center">
                            {/* Links: Zentrale / Support */}
                            <div className="flex flex-col items-center text-center lg:items-start lg:text-left w-full">
                                <div className="w-24 h-24 rounded-full bg-black border-2 border-[#333] flex items-center justify-center mb-4 overflow-hidden shadow-xl shadow-black/80">
                                     {logoToDisplay ? (
                                        <img src={logoToDisplay} alt="Logo" className="w-full h-full object-contain p-2" />
                                     ) : (
                                        <div className="text-[#E6C299] font-black text-2xl tracking-tighter drop-shadow-[0_0_8px_rgba(230,194,153,0.5)]">PE</div>
                                     )}
                                </div>
                                <h5 className="text-xl font-black text-pe-text dark:text-white tracking-wide mb-1">{generalContactInfo.name}</h5>
                                <p className="text-[10px] text-stone-500 dark:text-slate-500 mb-6 uppercase tracking-widest font-black">Support & Verwaltung</p>

                                <div className="w-full max-w-md lg:max-w-none space-y-3">
                                    <div className="bg-slate-800 border border-slate-700/50 rounded-xl p-3 shadow-inner text-center lg:text-left">
                                        <p className="text-pe-accent-text dark:text-[#E6C299] text-sm font-mono-numbers select-all font-bold tracking-wide">{generalContactInfo.value}</p>
                                    </div>
                                    <a href={generalContactInfo.link} className="w-full py-3 bg-gradient-to-b from-white to-stone-100 dark:from-[#222] dark:to-[#0a0a0a] border border-pe-border-strong dark:border-[#333] hover:from-[#E6C299] hover:to-[#b48a56] text-pe-muted dark:text-slate-300 hover:text-black rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-[0_0_15px_rgba(230,194,153,0.4)]">
                                        <MailIcon /> E-Mail schreiben
                                    </a>
                                </div>

                                <div className="w-full max-w-md lg:max-w-none mt-6 pt-6 border-t border-pe-border dark:border-[#222]">
                                    <p className="text-[10px] uppercase tracking-widest font-black text-pe-accent-text dark:text-[#E6C299] mb-2">Kommunikation</p>
                                    <h6 className="text-lg font-black text-pe-text dark:text-white tracking-wide uppercase mb-2">PEBase</h6>
                                    <p className="text-sm text-pe-muted dark:text-slate-300 leading-relaxed mb-4">
                                        Hier meldest du dich an. Die Kommunikation mit uns läuft ausschließlich über PEBase — nicht über WhatsApp.
                                    </p>
                                    <a
                                        href={PEBASE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full py-3 bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(230,194,153,0.35)]"
                                    >
                                        PEBase öffnen & anmelden
                                    </a>
                                </div>
                            </div>

                            {/* Rechts: Anleitungsvideo Hochformat */}
                            <div className="w-full max-w-[260px] sm:max-w-[280px] mx-auto lg:mx-0 shrink-0">
                                <p className="text-[10px] uppercase tracking-widest font-black text-stone-500 dark:text-slate-500 text-center mb-2">Installation & Nutzung</p>
                                <div className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden bg-black border border-pe-border dark:border-[#333] shadow-inner">
                                    <video
                                        controls
                                        playsInline
                                        preload="metadata"
                                        className="w-full h-full object-contain bg-black"
                                        src={`${PEBASE_VIDEO}#t=0.001`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. PYRAMIDE BASIS: COACHES GRID (3 spaltig) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        {teamContactInfo.map((contact) => {
                            const hasAnyLink = Boolean(contact.calendarLinkBasic || contact.calendarLinkInsider || contact.calendarLinkTeam);

                            return (
                                <div key={contact.id} className="carbon-panel carbon-weave-bg border-pe-border dark:border-[#2a2a2a] hover:border-[#E6C299]/30 rounded-2xl p-6 shadow-2xl transition-all duration-300 group flex flex-col items-center text-center h-full relative overflow-hidden">
                                    <div className="carbon-content flex flex-col items-center h-full w-full">
                                        <div className="mb-4 relative z-10">
                                            {contact.image ? (
                                                <img src={contact.image} alt={contact.name} className="w-24 h-24 rounded-full object-cover border-2 border-[#333] group-hover:border-[#E6C299] transition-colors duration-300 shadow-xl shadow-black/60" onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { const target = e.target as HTMLImageElement; target.onerror = null; target.src='https://placehold.co/150x150/2D2D2D/E6C299?text=Team'; }} />
                                            ) : (
                                                <div className="w-24 h-24 rounded-full bg-black border-2 border-[#333] flex items-center justify-center group-hover:border-[#E6C299] transition-colors duration-300 text-stone-500 dark:text-slate-500 shadow-xl shadow-black/60">
                                                    <span className="text-2xl font-black text-slate-400">{contact.firstName.charAt(0)}</span>
                                                </div>
                                            )}
                                            <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-4 border-white dark:border-[#111] rounded-full shadow-[0_0_10px_rgba(34,197,94,0.6)] animate-pulse"></div>
                                        </div>
                                        
                                        <h5 className="text-lg font-black text-pe-text dark:text-white tracking-wide mb-1 z-10">{contact.firstName}</h5>
                                        
                                        <div className="min-h-[3em] mb-4 flex flex-col items-center justify-start z-10">
                                            <p className="text-[10px] text-pe-accent-text dark:text-[#E6C299] uppercase tracking-widest font-black drop-shadow-[0_0_5px_rgba(230,194,153,0.2)]">{contact.role}</p>
                                            {contact.customRole && (
                                                <p className="text-[10px] text-stone-500 dark:text-slate-500 font-bold uppercase tracking-wider mt-1">({contact.customRole})</p>
                                            )}
                                        </div>
                                        
                                        <div className="w-full z-10 flex flex-col gap-3 mt-2">
                                            {contact.phone ? (
                                            <div className="bg-slate-800 border border-slate-700/50 rounded-xl p-2.5 shadow-inner">
                                                <p className="text-pe-accent-text dark:text-[#E6C299] text-sm font-mono-numbers font-bold select-all tracking-wide">{contact.phone}</p>
                                            </div>
                                            ) : null}
                                            
                                            {contact.link ? (
                                            <>
                                            <a href={contact.link} target="_blank" rel="noopener noreferrer" className="w-full py-2.5 bg-green-100/70 dark:bg-green-950/20 border border-green-300 dark:border-green-500/30 hover:bg-green-600 text-green-700 dark:text-green-400 hover:text-white rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]">
                                                <WhatsAppIcon /> WhatsApp Chat
                                            </a>
                                            <WhatsAppEmergencyNote />
                                            </>
                                            ) : null}

                                            {hasAnyLink && (
                                                <div className="pt-4 border-t border-pe-border dark:border-[#222] mt-2">
                                                    <div className="bg-amber-100/80 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-500/20 rounded-xl p-2.5 mb-4 text-center shadow-inner">
                                                        <p className="text-[10px] text-amber-900 dark:text-amber-400 leading-relaxed font-bold">
                                                            <span className="font-black uppercase tracking-widest text-amber-900 dark:text-amber-400 block mb-0.5">Wichtig:</span> Kontaktiere Deinen Betreuer vorher über PEBase, um zu checken, ob ein Meeting nötig ist. WhatsApp nur noch im Notfall.
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col gap-2">
                                                        {contact.calendarLinkBasic && (
                                                            <a href={contact.calendarLinkBasic} target="_blank" rel="noopener noreferrer" className="w-full min-h-[2.5rem] px-2 py-1.5 bg-gradient-to-b from-white to-stone-100 dark:from-[#222] dark:to-[#111] hover:from-stone-100 hover:to-stone-200 dark:hover:from-slate-800 dark:hover:to-slate-700 text-pe-muted dark:text-slate-300 border border-pe-border-strong dark:border-[#333] rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-1.5 shadow text-center leading-tight">
                                                                <span className="shrink-0"><CalendarIcon /></span>
                                                                <span className="text-center leading-tight">Allgemein / Solo<br /><span className="font-mono-numbers normal-case tracking-normal">(30 Min)</span></span>
                                                            </a>
                                                        )}
                                                        {contact.calendarLinkInsider && (
                                                            <a href={contact.calendarLinkInsider} target="_blank" rel="noopener noreferrer" className="w-full min-h-[2.5rem] px-2 py-1.5 bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(230,194,153,0.3)] text-center leading-tight">
                                                                <span className="shrink-0"><CalendarIcon /></span>
                                                                <span className="text-center leading-tight">Team & MA-Auswertung<br /><span className="font-mono-numbers normal-case tracking-normal">(60 Min)</span></span>
                                                            </a>
                                                        )}
                                                        {contact.calendarLinkTeam && (
                                                            <a href={contact.calendarLinkTeam} target="_blank" rel="noopener noreferrer" className="w-full py-1.5 bg-stone-100 dark:bg-black hover:bg-stone-200 dark:hover:bg-[#111] text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 rounded-lg border border-pe-border dark:border-[#222] text-[10px] font-black uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 shadow-inner">
                                                                <CalendarIcon /> Teammeeting (<span className="font-mono-numbers">90min</span>)
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </Section>
            </div>
        </Page>
    );
};

export default ContactPage;